'use client'

import { type ReactNode } from 'react'
import type { OptimizelyDecideOption } from '@optimizely/react-sdk'
import type { TransformedFeatureFlag } from '../utils/feature-flag-transform'
import useFeatureFlag from './use-feature-flag'

type RequireAtLeastOne<T1, T2> =
  | (T1 & (T2 extends object ? Partial<T2> : never))
  | (T2 & (T1 extends object ? Partial<T1> : never))

type Props = RequireAtLeastOne<
  { flagKey: string },
  { serverData: TransformedFeatureFlag | Record<string, never> }
> & {
  fallback?: ReactNode
  children: ReactNode
  trackOnly?: boolean
  decideOptions?: `${OptimizelyDecideOption}`[]
  disableGTMEvent?: boolean
}

/**
 * A wrapper component that conditionally renders its children
 * based on a feature flag's enabled state.
 *
 * **Requires either `flagKey` OR `serverData`**
 *
 * Supports both server-side and client-side feature flag checks for SSR + client hydration.
 *
 * @param flagKey - The feature flag key to evaluate client-side (e.g. `"new-checkout"`).
 * @param serverData - Pre-evaluated flag data from the server (`TransformedFeatureFlag`). Takes precedence over `flagKey` for the initial render.
 * @param fallback - Optional. Node to render when the flag is disabled. Defaults to `null`.
 * @param trackOnly - Optional. If `true`, always renders children regardless of flag state, used for tracking only.
 * @param decideOptions - Optional. Array of Optimizely decide options passed to `useFeatureFlag`.
 * @param disableGTMEvent - Optional. If `true`, suppresses the GTM event fired by `useFeatureFlag`. Defaults to `false`.
 *
 * @example
 * ```tsx
 * // Using flagKey (client-side only)
 * <FeatureFlag flagKey="new-checkout" fallback={<OldCheckout />}>
 *   <NewCheckout />
 * </FeatureFlag>
 * ```
 *
 * @example
 * ```tsx
 * // Using serverData (SSR + client hydration)
 * <FeatureFlag serverData={transformedFlagData} fallback={<OldCheckout />}>
 *   <NewCheckout />
 * </FeatureFlag>
 * ```
 *
 * @example
 * ```tsx
 * // Track only (always renders children, just fires GTM event)
 * <FeatureFlag flagKey="new-checkout" trackOnly>
 *   <NewCheckout />
 * </FeatureFlag>
 * ```
 *
 * @remarks
 * - **Server check**: Uses `serverData?.enabled` first if provided
 * - **Client check**: Uses `useFeatureFlag()` hook keyed on `serverData.flagKey` → `flagKey` → `''`
 * - Renders `fallback` (or `null`) if `serverData.enabled` is `false`, or if `serverData` is absent and the client flag is disabled
 * - `trackOnly` bypasses all enabled checks and always renders children
 */
const FeatureFlag = ({
  flagKey,
  fallback,
  serverData,
  trackOnly,
  decideOptions,
  disableGTMEvent = false,
  children,
}: Props) => {
  const { enabled: enabledOnServer, flagKey: serverFlagKey } = serverData ?? {}
  const { enabled: enabledOnClient } =
    useFeatureFlag(serverFlagKey || flagKey || '', disableGTMEvent, decideOptions) ?? {}

  if (trackOnly) {
    return children
  }

  if (enabledOnServer === false || (enabledOnServer === undefined && !enabledOnClient)) {
    return fallback ? fallback : null
  }

  return children
}

export default FeatureFlag
